import { toast } from '@heroui/react';
import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { Selection, SortDescriptor } from 'react-aria-components';
import { trpc } from '@/lib/trpc';
import { useAppSelector } from '@/stores/hooks';
import type { PaginationState, SortingState } from '@hs/ui';

import { cn } from '@heroui/react';
import {
  AlertDialog,
  Button,
  Chip,
  Dropdown,
  Label,
  ListBox,
  Pagination,
  SearchField,
  Separator,
} from '@heroui/react';
import { DataGrid, InlineSelect, Segment } from '@heroui-pro/react';
import type { DataGridColumn } from '@heroui-pro/react';
import { Funnel, LayoutColumns3 } from '@gravity-ui/icons';
import {
  IconPlus as Plus,
  IconRefresh as RefreshCw,
  IconDotsVertical as MoreVertical,
} from '@tabler/icons-react';
import { PAGE_DATA_GRID_ROW_ACTIONS_COLUMN_ID } from '@/components/layouts/page/page-data-grid-types';
import { ActivityEditorModal } from '@/pages/activities/components/editor/ActivityEditorModal';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

type ListScope = 'team' | 'my';

type ActivityTypeFilter = 'all' | 'external' | 'internal';

const ACTIVITY_TYPE_OPTIONS: { id: ActivityTypeFilter; label: string }[] = [
  { id: 'all', label: '全部' },
  { id: 'external', label: '市场活动' },
  { id: 'internal', label: '内部活动' },
];

const COLUMN_PICKER: { id: string; label: string }[] = [
  { id: 'title', label: '活动标题' },
  { id: 'status', label: '状态' },
  { id: 'mode', label: '形式' },
  { id: 'startTime', label: '开始时间' },
  { id: 'endTime', label: '结束时间' },
  { id: 'currentParticipants', label: '报名人数' },
];

const DEFAULT_PAGE_SIZE = 20;

function sortingToDescriptor(sorting: SortingState): SortDescriptor | undefined {
  const s = sorting[0];
  if (!s) return undefined;
  return {
    column: s.id,
    direction: s.desc ? 'descending' : 'ascending',
  };
}

function descriptorToSorting(descriptor: SortDescriptor | undefined): SortingState {
  if (!descriptor?.column) return [];
  return [
    {
      id: String(descriptor.column),
      desc: descriptor.direction === 'descending',
    },
  ];
}

function buildPaginationPages(totalPages: number, safePage: number): (number | 'ellipsis')[] {
  const pages: (number | 'ellipsis')[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (safePage > 3) pages.push('ellipsis');
    const start = Math.max(2, safePage - 1);
    const end = Math.min(totalPages - 1, safePage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (safePage < totalPages - 2) pages.push('ellipsis');
    pages.push(totalPages);
  }
  return pages;
}

/**
 * 活动列表组件（排版与 {@link MemberManagementPanel} 对齐：header + 工具条 + HeroUI Pro DataGrid + 分页）
 */
export function ActivitiesList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAppSelector((state) => state.auth.user);
  const userId = user?.id;

  const [listScope, setListScope] = React.useState<ListScope>('team');
  const [activityTypeFilter, setActivityTypeFilter] = React.useState<ActivityTypeFilter>('all');

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [searchKeyword, setSearchKeyword] = React.useState('');
  const [visibleColumnKeys, setVisibleColumnKeys] = React.useState<Selection>('all');
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = React.useState(false);
  const [activityToRemove, setActivityToRemove] = React.useState<{ id: string; title?: string | null } | null>(
    null
  );

  const editorParam = searchParams.get('editor');
  const editorOpen = Boolean(editorParam);
  const prevEditorOpenRef = React.useRef(false);
  const [editorMountKey, setEditorMountKey] = React.useState(0);

  React.useEffect(() => {
    if (editorOpen && !prevEditorOpenRef.current) {
      setEditorMountKey((k) => k + 1);
    }
    prevEditorOpenRef.current = editorOpen;
  }, [editorOpen]);

  const clearEditorSearchParams = React.useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('editor');
        next.delete('step');
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const handleEditorModalOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) clearEditorSearchParams();
    },
    [clearEditorSearchParams]
  );

  const handlePersistedActivityId = React.useCallback(
    (id: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('editor', id);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const sortBy = React.useMemo(() => {
    if (sorting.length === 0) return undefined;
    return sorting[0].id;
  }, [sorting]);

  const sortOrder = React.useMemo(() => {
    if (sorting.length === 0) return undefined;
    return sorting[0].desc ? 'desc' : 'asc';
  }, [sorting]);

  const isMyView = listScope === 'my';
  const effectiveSortBy = React.useMemo(() => {
    if (sortBy) return sortBy;
    return isMyView ? 'updatedAt' : 'startTime';
  }, [sortBy, isMyView]);

  const effectiveSortOrder = React.useMemo(() => {
    if (sortOrder) return sortOrder;
    return 'desc';
  }, [sortOrder]);

  const listQueryInput = React.useMemo(() => {
    const base = {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      title: searchKeyword || undefined,
      sortBy: effectiveSortBy as 'updatedAt' | 'startTime' | string,
      sortOrder: effectiveSortOrder as 'asc' | 'desc',
    };
    const typePart =
      activityTypeFilter === 'all' ? {} : { type: activityTypeFilter as 'external' | 'internal' };
    if (listScope === 'team') {
      return { ...base, ...typePart, status: 'published' as const };
    }
    return { ...base, ...typePart };
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    searchKeyword,
    effectiveSortBy,
    effectiveSortOrder,
    listScope,
    activityTypeFilter,
  ]);

  const listQuery = trpc.activities.list.useQuery(listQueryInput as never, {
    enabled: !!userId && !isMyView,
  });
  const listMyQuery = trpc.activities.listMy.useQuery(listQueryInput as never, {
    enabled: !!userId && isMyView,
  });

  const activeQuery = isMyView ? listMyQuery : listQuery;
  const { data, isLoading, refetch } = activeQuery;

  React.useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [listScope, activityTypeFilter]);

  type ActivitiesListData = NonNullable<typeof data>;
  type Activity = ActivitiesListData['items'][number];

  const activities = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageCount = React.useMemo(
    () => Math.max(1, Math.ceil(total / (pagination.pageSize || DEFAULT_PAGE_SIZE))),
    [total, pagination.pageSize]
  );

  const statusMap = {
    draft: { label: '草稿', color: 'warning' as const },
    published: { label: '已发布', color: 'success' as const },
    closed: { label: '已关闭', color: 'default' as const },
    canceled: { label: '已取消', color: 'danger' as const },
  };

  const modeMap = {
    offline: { label: '线下' },
    online: { label: '线上' },
  };

  const formatDateTime = React.useCallback((value: Date | string | null | undefined) => {
    if (!value) return '—';
    try {
      const date = typeof value === 'string' ? new Date(value) : value;
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const h = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      return `${y}-${m}-${d} ${h}:${min}`;
    } catch {
      return '—';
    }
  }, []);

  const handleEdit = React.useCallback(
    (row: Activity) => {
      const id = row.id;
      if (!id) return;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('editor', id);
          return next;
        },
        { replace: false }
      );
    },
    [setSearchParams]
  );

  const handleRemove = React.useCallback((row: Activity) => {
    if (!row.id) return;
    setActivityToRemove({ id: row.id, title: row.title });
    setIsRemoveDialogOpen(true);
  }, []);

  const renderActions = React.useCallback(
    (activity: Activity) => (
      <div className="flex justify-center">
        <Dropdown>
          <Button isIconOnly aria-label="操作" size="sm" variant="ghost">
            <MoreVertical className="size-4" stroke={1.5} />
          </Button>
          <Dropdown.Popover className="min-w-[140px]" placement="bottom end">
            <Dropdown.Menu>
              <Dropdown.Item id="edit" textValue="编辑" onAction={() => handleEdit(activity)}>
                <Label>编辑</Label>
              </Dropdown.Item>
              <Dropdown.Item
                id="stats"
                textValue="统计大屏"
                onAction={() => navigate(`/activities/${activity.id}/stats`)}
              >
                <Label>统计大屏</Label>
              </Dropdown.Item>
              <Dropdown.Item
                id="lottery"
                textValue="抽奖大屏"
                onAction={() => navigate(`/activities/${activity.id}/lottery`)}
              >
                <Label>抽奖大屏</Label>
              </Dropdown.Item>
              <Dropdown.Item
                id="leaderboard"
                textValue="邀请排行榜"
                onAction={() => navigate(`/activities/${activity.id}/leaderboard`)}
              >
                <Label>邀请排行榜</Label>
              </Dropdown.Item>
              <Dropdown.Item
                id="winners"
                textValue="中奖名单"
                onAction={() => navigate(`/activities/${activity.id}/winners`)}
              >
                <Label>中奖名单</Label>
              </Dropdown.Item>
              <Dropdown.Item
                id="delete"
                textValue="删除"
                variant="danger"
                onAction={() => handleRemove(activity)}
              >
                <Label>删除</Label>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown>
      </div>
    ),
    [handleEdit, handleRemove, navigate]
  );

  const allDataColumns = React.useMemo<DataGridColumn<Activity>[]>(
    () => [
      {
        id: 'title',
        accessorKey: 'title',
        header: '活动标题',
        minWidth: 300,
        allowsSorting: true,
        allowsResizing: true,
        cell: (row) => <span className="text-sm font-medium">{row.title}</span>,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: '状态',
        minWidth: 100,
        allowsSorting: true,
        allowsResizing: true,
        cell: (row) => {
          const status = row.status as keyof typeof statusMap;
          const info = statusMap[status] ?? { label: String(row.status ?? ''), color: 'default' as const };
          return (
            <Chip color={info.color} size="sm" variant="soft">
              <Chip.Label>{info.label}</Chip.Label>
            </Chip>
          );
        },
      },
      {
        id: 'mode',
        accessorKey: 'mode',
        header: '形式',
        minWidth: 88,
        allowsSorting: true,
        allowsResizing: true,
        cell: (row) => {
          const mode = row.mode as keyof typeof modeMap;
          const info = modeMap[mode] ?? { label: String(row.mode ?? '') };
          return (
            <Chip color="default" size="sm" variant="soft">
              <Chip.Label>{info.label}</Chip.Label>
            </Chip>
          );
        },
      },
      {
        id: 'startTime',
        accessorKey: 'startTime',
        header: '开始时间',
        minWidth: 160,
        allowsSorting: false,
        allowsResizing: true,
        cell: (row) => <span className="text-muted-foreground text-sm">{formatDateTime(row.startTime)}</span>,
      },
      {
        id: 'endTime',
        accessorKey: 'endTime',
        header: '结束时间',
        minWidth: 160,
        allowsSorting: false,
        allowsResizing: true,
        cell: (row) => <span className="text-muted-foreground text-sm">{formatDateTime(row.endTime)}</span>,
      },
      {
        id: 'currentParticipants',
        accessorKey: 'currentParticipants',
        header: '报名｜参与人数',
        minWidth: 120,
        allowsSorting: false,
        allowsResizing: true,
        cell: (row) => {
          const n = row.currentParticipants;
          const quota = row.quota;
          return (
            <span className="text-sm">{quota ? `${n ?? 0}/${quota}` : `${n ?? 0}`}</span>
          );
        },
      },
    ],
    [formatDateTime]
  );

  const actionsColumn = React.useMemo<DataGridColumn<Activity>>(
    () => ({
      id: PAGE_DATA_GRID_ROW_ACTIONS_COLUMN_ID,
      header: '',
      align: 'end',
      width: 70,
      minWidth: 70,
      maxWidth: 70,
      allowsSorting: false,
      allowsResizing: false,
      pinned: 'end',
      cell: (item) => (
        <div className="flex min-w-0 justify-end gap-1 overflow-hidden">{renderActions(item)}</div>
      ),
    }),
    [renderActions]
  );

  const visibleDataColumnIds = React.useMemo(() => {
    const allIds = COLUMN_PICKER.map((c) => c.id);
    if (visibleColumnKeys === 'all') return new Set(allIds);
    const s = visibleColumnKeys as Set<string>;
    if (s.size === 0) return new Set(allIds);
    const next = new Set<string>();
    for (const id of allIds) {
      if (s.has(id)) next.add(id);
    }
    return next.size > 0 ? next : new Set(allIds);
  }, [visibleColumnKeys]);

  const columns = React.useMemo(() => {
    const dataCols = allDataColumns.filter((c) => visibleDataColumnIds.has(String(c.id)));
    return [...dataCols, actionsColumn];
  }, [allDataColumns, visibleDataColumnIds, actionsColumn]);

  const dataGridColumnSignature = React.useMemo(() => {
    const sorted = [...visibleDataColumnIds].sort().join('\u001f');
    return [sorted, PAGE_DATA_GRID_ROW_ACTIONS_COLUMN_ID].join('\u001e');
  }, [visibleDataColumnIds]);

  const sortDescriptor = React.useMemo(() => sortingToDescriptor(sorting), [sorting]);

  const sortableColumnIds = React.useMemo(() => new Set(['title', 'status', 'mode']), []);

  const columnsWithRowHeader = React.useMemo(() => {
    const first = columns.find((c) => c.id !== PAGE_DATA_GRID_ROW_ACTIONS_COLUMN_ID);
    const fh = first?.id;
    return columns.map((c) => ({
      ...c,
      isRowHeader: c.id === fh,
      allowsSorting: sortableColumnIds.has(String(c.id)) ? true : (c.allowsSorting ?? false),
    }));
  }, [columns, sortableColumnIds]);

  const handleSortChange = React.useCallback((d: SortDescriptor) => {
    setSorting(descriptorToSorting(d));
  }, []);

  const columnPickerSelectedKeys = React.useMemo((): Selection => {
    if (visibleColumnKeys === 'all') return new Set(COLUMN_PICKER.map((c) => c.id));
    const s = visibleColumnKeys as Set<string>;
    if (s.size === 0) return new Set(COLUMN_PICKER.map((c) => c.id));
    return s;
  }, [visibleColumnKeys]);

  const handleRefresh = React.useCallback(() => void refetch(), [refetch]);

  const safePage = Math.min(pagination.pageIndex + 1, pageCount);
  const paginationPages = React.useMemo(
    () => buildPaginationPages(pageCount, safePage),
    [pageCount, safePage]
  );

  const pageSizes = [10, 20, 50, 100];

  const handleCreate = React.useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('editor', 'new');
        return next;
      },
      { replace: false }
    );
  }, [setSearchParams]);

  const deleteActivityMutation = trpc.activities.delete.useMutation({
    onSuccess: () => {
      toast.success('活动删除成功');
      setIsRemoveDialogOpen(false);
      setActivityToRemove(null);
      void refetch();
    },
  });

  const handleConfirmRemove = React.useCallback(() => {
    if (activityToRemove?.id) {
      deleteActivityMutation.mutate({ id: activityToRemove.id });
    }
  }, [activityToRemove, deleteActivityMutation]);

  const emptyMessage = React.useMemo(() => {
    if (listScope === 'team') {
      if (activityTypeFilter === 'external') return '暂无符合条件的市场活动';
      if (activityTypeFilter === 'internal') return '暂无符合条件的内部活动';
      return '暂无符合条件的团队活动';
    }
    if (activityTypeFilter === 'external') return '暂无符合条件的市场活动';
    if (activityTypeFilter === 'internal') return '暂无符合条件的内部活动';
    return '暂无符合条件的我的活动';
  }, [listScope, activityTypeFilter]);

  const activityTypeSelectedKeys = React.useMemo(
    (): Selection => new Set([activityTypeFilter]),
    [activityTypeFilter]
  );

  const handleActivityTypeSelectionChange = React.useCallback((keys: Selection) => {
    if (keys === 'all') return;
    const s = keys as Set<string>;
    const next = [...s][0] as ActivityTypeFilter | undefined;
    if (next === 'all' || next === 'external' || next === 'internal') {
      setActivityTypeFilter(next);
    }
  }, []);

  const handleListScopeChange = React.useCallback((key: React.Key) => {
    const k = String(key) as ListScope;
    if (k === 'team' || k === 'my') setListScope(k);
  }, []);

  return (
    <>
      <div className="flex h-full min-h-0 flex-col rounded-4xl">
        <header className="shrink-0 px-4 py-3">
          <Segment selectedKey={listScope} onSelectionChange={handleListScopeChange}>
            <Segment.Item id="team">
              <Segment.Separator />
              团队活动
            </Segment.Item>
            <Segment.Item id="my">
              <Segment.Separator />
              我的活动
            </Segment.Item>
          </Segment>
        </header>

        <div className={cn('flex min-h-0 w-full flex-1 flex-col p-4')}>
          <div className="flex min-h-0 flex-1 flex-col">
            <div
              className={cn(
                'grid min-h-0 min-w-0 w-full flex-1 gap-y-3',
                'grid-rows-[auto_minmax(0,1fr)_auto]'
              )}
              data-slot="activities-grid-main"
            >
              <div className="relative z-10 flex min-w-0 shrink-0 flex-col gap-3">
                <div
                  className="flex w-full min-w-0 shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2"
                  data-slot="activities-toolbar"
                >
                  <div
                    className="flex min-w-0 flex-wrap items-center justify-start gap-2"
                    data-slot="activities-action-toolbar"
                  >
                    <SearchField
                      aria-label="搜索活动"
                      value={searchKeyword}
                      onChange={(v) => {
                        setSearchKeyword(v);
                        setPagination((p) => ({ ...p, pageIndex: 0 }));
                      }}
                    >
                      <SearchField.Group>
                        <SearchField.SearchIcon />
                        <SearchField.Input
                          className="w-[min(100%,220px)] min-w-[160px]"
                          placeholder="搜索活动标题..."
                        />
                        <SearchField.ClearButton />
                      </SearchField.Group>
                    </SearchField>

                    <Dropdown>
                      <Button size="sm" variant="secondary">
                        <Funnel className="size-4 shrink-0" />
                        活动类型
                      </Button>
                      <Dropdown.Popover>
                        <Dropdown.Menu
                          disallowEmptySelection
                          selectedKeys={activityTypeSelectedKeys}
                          selectionMode="single"
                          onSelectionChange={handleActivityTypeSelectionChange}
                        >
                          {ACTIVITY_TYPE_OPTIONS.map((opt) => (
                            <Dropdown.Item key={opt.id} id={opt.id} textValue={opt.label}>
                              <Label>{opt.label}</Label>
                              <Dropdown.ItemIndicator />
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown.Popover>
                    </Dropdown>

                    <Dropdown>
                      <Button size="sm" variant="secondary">
                        <LayoutColumns3 />
                        列显示
                      </Button>
                      <Dropdown.Popover>
                        <Dropdown.Menu
                          disallowEmptySelection
                          selectedKeys={columnPickerSelectedKeys}
                          selectionMode="multiple"
                          onSelectionChange={setVisibleColumnKeys}
                        >
                          {COLUMN_PICKER.map((f) => (
                            <Dropdown.Item key={f.id} id={f.id} textValue={f.label}>
                              <Label>{f.label}</Label>
                              <Dropdown.ItemIndicator />
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown.Popover>
                    </Dropdown>

                    <Button variant="secondary" onPress={handleRefresh}>
                      <RefreshCw className="size-4" stroke={1.5} />
                      刷新
                    </Button>
                  </div>

                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                    <Button onPress={handleCreate}>
                      <Plus className="size-4" stroke={1.5} />
                      创建
                    </Button>
                  </div>
                </div>
              </div>

              <div className="relative min-h-0 min-w-0 overflow-hidden">
                <div className="absolute inset-0 flex min-h-0 min-w-0 flex-col">
                  {isLoading ? (
                    <div className="bg-background/60 absolute inset-0 z-10 flex items-center justify-center text-sm">
                      加载中…
                    </div>
                  ) : null}
                  <DataGrid<Activity>
                    key={dataGridColumnSignature}
                    allowsColumnResize
                    aria-label="活动列表"
                    className="flex h-full min-h-0 min-w-0 flex-col !overflow-visible"
                    columns={columnsWithRowHeader}
                    contentClassName="min-w-0 !overflow-visible"
                    data={activities}
                    getRowId={(item) => item.id ?? ''}
                    renderEmptyState={() => (
                      <div className="text-muted-foreground py-8 text-center text-sm">{emptyMessage}</div>
                    )}
                    scrollContainerClassName={cn(
                      'h-full max-h-full min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain',
                      '[&_[data-slot=table-resizable-container]]:overflow-visible',
                      '[&_[data-slot=table-content]]:!overflow-visible',
                      '[&_thead_th]:bg-[var(--surface-secondary)] [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-20',
                      '[&_th]:min-w-0 [&_td]:min-w-0',
                      '[&_[role=columnheader]]:min-w-0 [&_[role=gridcell]]:min-w-0'
                    )}
                    selectionMode="none"
                    showSelectionCheckboxes={false}
                    sortDescriptor={sortDescriptor}
                    variant="primary"
                    onSortChange={handleSortChange}
                  />
                </div>
              </div>

              <div
                className="text-muted-foreground flex min-w-0 shrink-0 flex-nowrap items-center justify-between gap-3 overflow-x-auto text-sm"
                data-slot="activities-pagination"
              >
                <div className="flex w-fit shrink-0 items-center">
                  <Pagination className="w-fit max-w-none" size="sm">
                    <Pagination.Content className="flex w-fit min-w-0">
                      <Pagination.Item>
                        <Pagination.Previous
                          isDisabled={safePage <= 1}
                          onPress={() =>
                            setPagination((p) => ({
                              ...p,
                              pageIndex: Math.max(0, p.pageIndex - 1),
                            }))
                          }
                        >
                          <Pagination.PreviousIcon />
                        </Pagination.Previous>
                      </Pagination.Item>
                      {paginationPages.map((p, i) =>
                        p === 'ellipsis' ? (
                          <Pagination.Item key={`e-${i}`}>
                            <Pagination.Ellipsis />
                          </Pagination.Item>
                        ) : (
                          <Pagination.Item key={p}>
                            <Pagination.Link
                              isActive={p === safePage}
                              onPress={() =>
                                setPagination((prev) => ({
                                  ...prev,
                                  pageIndex: (p as number) - 1,
                                }))
                              }
                            >
                              {p}
                            </Pagination.Link>
                          </Pagination.Item>
                        )
                      )}
                      <Pagination.Item>
                        <Pagination.Next
                          isDisabled={safePage >= pageCount}
                          onPress={() =>
                            setPagination((p) => ({
                              ...p,
                              pageIndex: Math.min(pageCount - 1, p.pageIndex + 1),
                            }))
                          }
                        >
                          <Pagination.NextIcon />
                        </Pagination.Next>
                      </Pagination.Item>
                    </Pagination.Content>
                  </Pagination>
                </div>

                <div className="flex h-4 w-fit shrink-0 flex-nowrap items-center gap-3 whitespace-nowrap">
                  <InlineSelect
                    aria-label="每页条数"
                    value={String(pagination.pageSize)}
                    onChange={(v) => {
                      if (v) {
                        setPagination((p) => ({
                          ...p,
                          pageSize: Number(v),
                          pageIndex: 0,
                        }));
                      }
                    }}
                  >
                    <InlineSelect.Trigger>
                      <span className="text-muted">每页</span>
                      <InlineSelect.Value />
                      <InlineSelect.Indicator />
                    </InlineSelect.Trigger>
                    <InlineSelect.Popover className="w-[88px]">
                      <ListBox>
                        {pageSizes.map((n) => (
                          <ListBox.Item key={n} id={String(n)} textValue={String(n)}>
                            {n}
                            <ListBox.ItemIndicator />
                          </ListBox.Item>
                        ))}
                      </ListBox>
                    </InlineSelect.Popover>
                  </InlineSelect>
                  <Separator className="!h-4 shrink-0" orientation="vertical" />
                  <span className="shrink-0 text-muted">
                    第 {total === 0 ? 0 : (safePage - 1) * pagination.pageSize + 1}–
                    {Math.min(safePage * pagination.pageSize, total)} 条，共 {total} 条
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AlertDialog isOpen={isRemoveDialogOpen} onOpenChange={setIsRemoveDialogOpen}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除活动「{activityToRemove?.title ?? ''}」吗？删除后不可恢复。
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button
                  variant="secondary"
                  onPress={() => {
                    setIsRemoveDialogOpen(false);
                    setActivityToRemove(null);
                  }}
                >
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deleteActivityMutation.isPending}
                  onPress={() => void handleConfirmRemove()}
                >
                  {deleteActivityMutation.isPending ? '删除中…' : '确认删除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>

      <ActivityEditorModal
        isOpen={editorOpen}
        editorParam={editorParam}
        editorMountKey={editorMountKey}
        onOpenChange={handleEditorModalOpenChange}
        onClose={clearEditorSearchParams}
        onPersistedActivityId={handlePersistedActivityId}
      />
    </>
  );
}
